import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, fmtMoney, fmtDate } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { MetricCard } from '@/components/MetricCard';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { FileText, CheckCircle2, Clock, DollarSign, Users, Package, Plus, ArrowRight } from 'lucide-react';
import { QuoteStatusBadge } from '@/components/QuoteStatusBadge';

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, q] = await Promise.all([api.get('/dashboard/stats'), api.get('/quotes')]);
        setStats(s.data);
        setRecent((q.data || []).slice(0, 5));
      } finally { setLoading(false); }
    })();
  }, []);

  const isExpired = (q) => new Date(q.expiry_date) < new Date();

  return (
    <div>
      <PageHeader
        title="Dashboard"
        subtitle="Resumen de tu actividad comercial."
        actions={
          <Link to="/quotes/new">
            <Button className="btn-primary h-10" data-testid="dashboard-new-quote-button">
              <Plus className="mr-1.5 h-4 w-4" /> Nuevo presupuesto
            </Button>
          </Link>
        }
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard label="Total presupuestos" value={stats?.total_quotes ?? '—'} icon={FileText} testid="metric-total-quotes" hint={loading ? 'Cargando…' : `${stats?.draft_quotes ?? 0} borradores`} />
        <MetricCard label="Aceptados" value={stats?.accepted_quotes ?? '—'} icon={CheckCircle2} testid="metric-accepted-quotes" hint={fmtMoney(stats?.accepted_amount || 0)} />
        <MetricCard label="Pendientes" value={(stats?.sent_quotes ?? 0)} icon={Clock} testid="metric-sent-quotes" hint="Enviados sin respuesta" />
        <MetricCard label="Monto total" value={fmtMoney(stats?.total_amount || 0)} icon={DollarSign} testid="metric-total-amount" hint="Todos los presupuestos" />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="glass border-[var(--glass-border)] lg:col-span-2">
          <CardContent className="p-5">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-display text-lg font-semibold">Presupuestos recientes</h2>
              <Link to="/quotes" className="text-sm text-[hsl(var(--primary))] hover:underline" data-testid="dashboard-view-all-quotes">Ver todos</Link>
            </div>
            {recent.length === 0 ? (
              <EmptyState
                icon={FileText}
                title="Aún no hay presupuestos"
                description="Crea tu primer presupuesto en menos de un minuto."
                action={<Link to="/quotes/new"><Button className="btn-primary"><Plus className="mr-1.5 h-4 w-4" /> Crear presupuesto</Button></Link>}
              />
            ) : (
              <div className="space-y-2">
                {recent.map((q) => (
                  <Link
                    key={q.id}
                    to={`/quotes/${q.id}`}
                    data-testid={`dashboard-quote-row-${q.quote_number}`}
                    className="group flex items-center justify-between rounded-lg border border-transparent bg-[var(--surface-1)] px-4 py-3 transition-colors hover:border-[var(--glass-border)] hover:bg-[var(--surface-2)]"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-medium tabular-nums">{q.quote_number}</span>
                        <QuoteStatusBadge status={q.status} expired={isExpired(q)} />
                      </div>
                      <div className="mt-0.5 truncate text-sm text-muted-foreground">{q.client_snapshot?.name}</div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="font-mono text-sm font-semibold tabular-nums">{fmtMoney(q.total)}</div>
                        <div className="text-xs text-muted-foreground">{fmtDate(q.issue_date)}</div>
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-foreground" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card className="glass border-[var(--glass-border)]">
            <CardContent className="p-5">
              <h3 className="font-display text-base font-semibold">Accesos rápidos</h3>
              <div className="mt-3 space-y-2">
                <Link to="/catalog" data-testid="dashboard-quick-catalog" className="flex items-center justify-between rounded-lg bg-[var(--surface-1)] px-3 py-2.5 text-sm transition-colors hover:bg-[var(--surface-2)]">
                  <span className="flex items-center gap-2"><Package className="h-4 w-4 text-[hsl(var(--primary))]" /> Catálogo</span>
                  <span className="font-mono text-xs text-muted-foreground">{stats?.catalog_count ?? 0}</span>
                </Link>
                <Link to="/clients" data-testid="dashboard-quick-clients" className="flex items-center justify-between rounded-lg bg-[var(--surface-1)] px-3 py-2.5 text-sm transition-colors hover:bg-[var(--surface-2)]">
                  <span className="flex items-center gap-2"><Users className="h-4 w-4 text-[hsl(var(--primary))]" /> Clientes</span>
                  <span className="font-mono text-xs text-muted-foreground">{stats?.clients_count ?? 0}</span>
                </Link>
              </div>
            </CardContent>
          </Card>
          <Card className="glass border-[var(--glass-border)]">
            <CardContent className="p-5">
              <h3 className="font-display text-base font-semibold">Seguridad</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Cada presupuesto incluye un <span className="text-foreground">QR de verificación</span> con firma criptográfica HMAC-SHA256.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
